/** Shared "full image, natural aspect ratio, never cropped" sizing math
 * (VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2 §2 —
 * "图片必须完整显示...保持原始 aspect ratio...禁止 object-fit: cover").
 *
 * Used by every Media Card's media body (`ImageAssetNode`,
 * `GenerateImageNode`, `GenerateVideoNode`'s top region) so the outer
 * box itself takes on the image/video's real shape — landscape cards go
 * wide, portrait cards go tall, square stays square — instead of
 * cropping into one fixed box. Still clamped to a sane min/max so one
 * extreme panorama/receipt-shaped image can never dominate the whole
 * Canvas (spec §2 "避免极端超长图片占满整个 Canvas... 允许 letterbox 但禁止
 * 裁掉图片内容"); `object-fit: contain` on the actual `<img>`/`<video>`
 * is what performs that letterboxing once clamped. */

export interface MediaBoxSize {
  width: number
  height: number
}

const MAX_DIM = 320
const MIN_DIM = 150
const HARD_MAX = 460

export function computeMediaBoxSize(naturalWidth: number, naturalHeight: number): MediaBoxSize {
  if (!naturalWidth || !naturalHeight) return { width: 260, height: 168 }
  const ratio = naturalWidth / naturalHeight

  let width = ratio >= 1 ? MAX_DIM : MAX_DIM * ratio
  let height = ratio >= 1 ? MAX_DIM / ratio : MAX_DIM

  if (width < MIN_DIM) {
    const scale = MIN_DIM / width
    width = MIN_DIM
    height *= scale
  }
  if (height < MIN_DIM) {
    const scale = MIN_DIM / height
    height = MIN_DIM
    width *= scale
  }
  if (width > HARD_MAX) {
    const scale = HARD_MAX / width
    width = HARD_MAX
    height *= scale
  }
  if (height > HARD_MAX) {
    const scale = HARD_MAX / height
    height = HARD_MAX
    width *= scale
  }
  return { width: Math.round(width), height: Math.round(height) }
}

/** Collapsed Generate Image card sizing — width and height stay in sync when max-height clamps. */
export const COLLAPSED_IMAGE_MAX_HEIGHT = 200
export const COLLAPSED_IMAGE_HEADER_MIN_WIDTH = 200

export interface CollapsedImageDisplaySize {
  /** Outer card width (header + border); may exceed image width for header min. */
  cardWidth: number
  /** Result preview width — matches displayed image width. */
  imageWidth: number
  /** Result preview height — matches displayed image height. */
  imageHeight: number
}

export function computeCollapsedImageDisplaySize(
  naturalWidth: number,
  naturalHeight: number,
  options?: {
    maxHeight?: number
    headerMinWidth?: number
    fallbackWidth?: number
    fallbackHeight?: number
  },
): CollapsedImageDisplaySize {
  const maxHeight = options?.maxHeight ?? COLLAPSED_IMAGE_MAX_HEIGHT
  const headerMinWidth = options?.headerMinWidth ?? COLLAPSED_IMAGE_HEADER_MIN_WIDTH
  const fallbackWidth = options?.fallbackWidth ?? 260
  const fallbackHeight = options?.fallbackHeight ?? 168

  if (!naturalWidth || !naturalHeight) {
    return { cardWidth: fallbackWidth, imageWidth: fallbackWidth, imageHeight: fallbackHeight }
  }

  const box = computeMediaBoxSize(naturalWidth, naturalHeight)
  let imageWidth = box.width
  let imageHeight = box.height

  if (imageHeight > maxHeight) {
    const scale = maxHeight / imageHeight
    imageWidth = Math.round(imageWidth * scale)
    imageHeight = maxHeight
  }

  const cardWidth = Math.max(headerMinWidth, imageWidth)
  return { cardWidth, imageWidth, imageHeight }
}
